import { useState, useEffect, useCallback, useRef } from 'react';
import { SopAPI } from '../utils/api';
import { useAuth } from '../components/AuthProvider';
import {
  DOC_ORG_ID,
  DOCS_META_TAG,
  createEmptyOrganization,
  parseOrganization,
  serializeOrganization,
  generateFolderId,
  isAncestorFolder,
  applyDeleteFolders,
  getChildFolders,
  getFolderPath,
  placeFolderAmongSiblings,
  foldersOrderEqual,
} from '../utils/docOrganization';
import { reportSaveStatus } from '../utils/saveIndicatorBridge';
import {
  resetDocOrgHistory,
  pushDocOrgHistory,
  takeDocOrgUndo,
  takeDocOrgRedo,
} from './docOrgHistory';

const ORG_CHANGED_EVENT = 'fokus:doc-org-changed';

const isNotFoundError = (e) => /not found|HTTP 404/i.test(e?.message || '');

let orgCache = {
  workspaceId: null,
  org: null,
  ready: false,
  recordExists: false,
};
const cacheListeners = new Set();
let orgInflight = { workspaceId: null, promise: null };

function cacheHit(workspaceId) {
  return orgCache.workspaceId === workspaceId && orgCache.ready && orgCache.org;
}

function writeOrgCache(workspaceId, { org, ready, recordExists }) {
  orgCache = { workspaceId, org, ready, recordExists };
  cacheListeners.forEach((fn) => fn(orgCache));
}

function applyCacheEntry(entry, setOrg, setReady, readyRef, recordExistsRef) {
  setOrg(entry.org);
  setReady(entry.ready);
  readyRef.current = entry.ready;
  recordExistsRef.current = entry.recordExists;
}

function fetchOrganization(workspaceId) {
  if (orgInflight.workspaceId === workspaceId && orgInflight.promise) {
    return orgInflight.promise;
  }
  const promise = SopAPI.get(DOC_ORG_ID)
    .then((doc) => {
      const next = {
        org: parseOrganization(doc),
        ready: true,
        recordExists: true,
      };
      writeOrgCache(workspaceId, next);
      return next;
    })
    .catch((e) => {
      if (!isNotFoundError(e)) throw e;
      const next = {
        org: createEmptyOrganization(),
        ready: true,
        recordExists: false,
      };
      writeOrgCache(workspaceId, next);
      return next;
    })
    .finally(() => {
      if (orgInflight.promise === promise) {
        orgInflight = { workspaceId: null, promise: null };
      }
    });
  orgInflight = { workspaceId, promise };
  return promise;
}

/**
 * useDocOrganization — nested folders for Docs / Atlas, persisted in a hidden
 * reserved doc record (see utils/docOrganization.js).
 *
 * Mutations are optimistic (state updates instantly) and saves are chained
 * so rapid edits can't race each other. Sidebar and Docs share one org
 * record — one download, cache for the rest.
 *
 * Until the initial load settles, `ready` is false and mutations are no-ops
 * — this prevents a transient network failure from clobbering the stored
 * organization with an empty one.
 */
export function useDocOrganization() {
  const { workspaceId, loading: authLoading } = useAuth();
  const hit = cacheHit(workspaceId);

  const [org, setOrg] = useState(() =>
    hit ? orgCache.org : createEmptyOrganization()
  );
  const [ready, setReady] = useState(hit);
  const readyRef = useRef(hit);
  const recordExistsRef = useRef(hit ? orgCache.recordExists : false);
  const saveChainRef = useRef(Promise.resolve());
  const instanceIdRef = useRef(`doc-org-${Math.random().toString(36).slice(2, 11)}`);
  const orgRef = useRef(org);
  orgRef.current = org;

  const load = useCallback(async () => {
    try {
      const next = await fetchOrganization(workspaceId);
      applyCacheEntry(next, setOrg, setReady, readyRef, recordExistsRef);
    } catch (e) {
      console.warn('Doc organization failed to load (mutations disabled):', e);
    }
  }, [workspaceId]);

  useEffect(() => {
    const onCache = (entry) => {
      if (entry.workspaceId !== workspaceId || !entry.org) return;
      applyCacheEntry(entry, setOrg, setReady, readyRef, recordExistsRef);
    };
    cacheListeners.add(onCache);
    return () => cacheListeners.delete(onCache);
  }, [workspaceId]);

  useEffect(() => {
    if (authLoading) return;
    resetDocOrgHistory(workspaceId);
    if (cacheHit(workspaceId)) {
      applyCacheEntry(orgCache, setOrg, setReady, readyRef, recordExistsRef);
      return;
    }
    readyRef.current = false;
    setReady(false);
    recordExistsRef.current = false;
    setOrg(createEmptyOrganization());
    load();
  }, [workspaceId, authLoading, load]);

  useEffect(() => {
    const onChanged = (e) => {
      if (e.detail?.sourceId === instanceIdRef.current) return;
      if (cacheHit(workspaceId)) {
        applyCacheEntry(orgCache, setOrg, setReady, readyRef, recordExistsRef);
        return;
      }
      load();
    };
    window.addEventListener(ORG_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ORG_CHANGED_EVENT, onChanged);
  }, [load, workspaceId]);

  const queuePersist = useCallback((nextOrg) => {
    const doSave = async () => {
      reportSaveStatus('saving');
      const payload = { body: serializeOrganization(nextOrg) };
      if (recordExistsRef.current) {
        try {
          await SopAPI.update(DOC_ORG_ID, payload);
        } catch (e) {
          if (!isNotFoundError(e)) throw e;
          recordExistsRef.current = false;
        }
      }
      if (!recordExistsRef.current) {
        await SopAPI.create({
          id: DOC_ORG_ID,
          title: 'Doc organization (system)',
          icon: '🗂️',
          tags: [DOCS_META_TAG],
          ...payload,
        });
        recordExistsRef.current = true;
      }
      writeOrgCache(workspaceId, {
        org: orgRef.current,
        ready: true,
        recordExists: recordExistsRef.current,
      });
      window.dispatchEvent(
        new CustomEvent(ORG_CHANGED_EVENT, {
          detail: { sourceId: instanceIdRef.current },
        })
      );
      reportSaveStatus('saved');
    };
    saveChainRef.current = saveChainRef.current
      .then(doSave)
      .catch((e) => {
        console.error('Failed to save doc organization:', e);
        reportSaveStatus('idle');
      });
  }, [workspaceId]);

  const rememberOrg = useCallback(
    (next) => {
      writeOrgCache(workspaceId, {
        org: next,
        ready: true,
        recordExists: recordExistsRef.current,
      });
    },
    [workspaceId]
  );

  const mutate = useCallback(
    (updater, { record = true } = {}) => {
      if (!readyRef.current) return;
      setOrg((prev) => {
        const next = updater(prev);
        if (next === prev) return prev;
        if (record) pushDocOrgHistory(prev);
        rememberOrg(next);
        queuePersist(next);
        return next;
      });
    },
    [queuePersist, rememberOrg]
  );

  const undoOrganization = useCallback(() => {
    if (!readyRef.current) return false;
    const prev = takeDocOrgUndo(orgRef.current);
    if (!prev) return false;
    setOrg(prev);
    rememberOrg(prev);
    queuePersist(prev);
    return true;
  }, [queuePersist, rememberOrg]);

  const redoOrganization = useCallback(() => {
    if (!readyRef.current) return false;
    const next = takeDocOrgRedo(orgRef.current);
    if (!next) return false;
    setOrg(next);
    rememberOrg(next);
    queuePersist(next);
    return true;
  }, [queuePersist, rememberOrg]);

  /** Create a folder under `parentId` (null = root). */
  const createFolder = useCallback(
    (name, parentId = null) => {
      const id = generateFolderId();
      const trimmed = (name || '').trim() || 'New folder';
      mutate((prev) => ({
        ...prev,
        folders: [
          ...prev.folders,
          { id, name: trimmed, parentId: parentId || null },
        ],
      }));
      return id;
    },
    [mutate]
  );

  const renameFolder = useCallback(
    (folderId, name) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return;
      mutate((prev) => ({
        ...prev,
        folders: prev.folders.map((f) =>
          f.id === folderId ? { ...f, name: trimmed } : f
        ),
      }));
    },
    [mutate]
  );

  /**
   * Delete folders. Nested folders go with them by default; docs move to the
   * leftover parent (or the library root). Docs themselves are never deleted.
   */
  const deleteFolders = useCallback(
    (folderIds, options) => {
      mutate((prev) => applyDeleteFolders(prev, folderIds, options));
    },
    [mutate]
  );

  const deleteFolder = useCallback(
    (folderId, options) =>
      deleteFolders(folderId ? [folderId] : [], options),
    [deleteFolders]
  );

  /**
   * Move one or more folders under a new parent (null = root) in a single persist.
   * Only the topmost selected folders move (nested descendants stay under them).
   * Refuses cycles. Dropping onto a folder that is itself in the set nests the
   * others into it.
   */
  const moveFolders = useCallback(
    (folderIds, newParentId) => {
      const requested = [...new Set((folderIds || []).filter(Boolean))];
      if (requested.length === 0) return;
      mutate((prev) => {
        const parent = newParentId || null;
        if (parent && !prev.folders.some((f) => f.id === parent)) return prev;

        const movers = requested.filter((id) => id !== parent);
        const topmost = movers.filter(
          (id) =>
            !movers.some(
              (other) => other !== id && isAncestorFolder(prev.folders, id, other)
            )
        );

        let folders = prev.folders;
        let changed = false;
        for (const folderId of topmost) {
          const folder = folders.find((f) => f.id === folderId);
          if (!folder) continue;
          if ((folder.parentId || null) === parent) continue;
          if (parent && isAncestorFolder(folders, parent, folderId)) continue;
          folders = folders.map((f) =>
            f.id === folderId ? { ...f, parentId: parent } : f
          );
          changed = true;
        }
        return changed ? { ...prev, folders } : prev;
      });
    },
    [mutate]
  );

  const moveFolder = useCallback(
    (folderId, newParentId) => moveFolders(folderId ? [folderId] : [], newParentId),
    [moveFolders]
  );

  /**
   * Place one or more folders before/after `targetId` (same or new parent).
   * Topmost movers stay a block; descendants ride along.
   */
  const moveFoldersNextTo = useCallback(
    (folderIds, targetId, position) => {
      const requested = [...new Set((folderIds || []).filter(Boolean))];
      if (requested.length === 0 || !targetId) return;
      mutate((prev) => {
        const target = prev.folders.find((f) => f.id === targetId);
        if (!target) return prev;
        const parent = target.parentId || null;
        const movers = requested.filter((id) => id !== targetId);
        const topmost = movers.filter(
          (id) =>
            !movers.some(
              (other) => other !== id && isAncestorFolder(prev.folders, id, other)
            )
        );
        const ordered = prev.folders
          .map((f) => f.id)
          .filter((id) => topmost.includes(id));

        let folders = prev.folders;
        let changed = false;
        let anchor = targetId;
        let pos = position === 'before' ? 'before' : 'after';
        for (const folderId of ordered) {
          if (parent === folderId) continue;
          if (parent && isAncestorFolder(folders, parent, folderId)) continue;
          const nextFolders = placeFolderAmongSiblings(folders, folderId, {
            parentId: parent,
            beforeId: pos === 'before' ? anchor : null,
            afterId: pos === 'after' ? anchor : null,
          });
          if (!foldersOrderEqual(folders, nextFolders)) {
            folders = nextFolders;
            changed = true;
          }
          anchor = folderId;
          pos = 'after';
        }
        return changed ? { ...prev, folders } : prev;
      });
    },
    [mutate]
  );

  /** Place a folder before/after another (same or new parent, including root). */
  const moveFolderNextTo = useCallback(
    (folderId, targetId, position) =>
      moveFoldersNextTo(folderId ? [folderId] : [], targetId, position),
    [moveFoldersNextTo]
  );

  /** Move a doc into a folder (or out of all folders with `null`). */
  const moveDocToFolder = useCallback(
    (docId, folderId) => {
      mutate((prev) => {
        if ((prev.docFolders[docId] || null) === (folderId || null)) return prev;
        if (folderId && !prev.folders.some((f) => f.id === folderId)) return prev;
        const docFolders = { ...prev.docFolders };
        if (folderId) docFolders[docId] = folderId;
        else delete docFolders[docId];
        return { ...prev, docFolders };
      });
    },
    [mutate]
  );

  const getDocFolderId = useCallback(
    (docId) => org.docFolders[docId] || null,
    [org.docFolders]
  );

  const getChildFoldersOf = useCallback(
    (parentId = null) => getChildFolders(org.folders, parentId),
    [org.folders]
  );

  const getPathToFolder = useCallback(
    (folderId) => getFolderPath(org.folders, folderId),
    [org.folders]
  );

  return {
    ready,
    folders: org.folders,
    docFolders: org.docFolders,
    getDocFolderId,
    getChildFolders: getChildFoldersOf,
    getFolderPath: getPathToFolder,
    createFolder,
    renameFolder,
    deleteFolder,
    deleteFolders,
    moveFolder,
    moveFolders,
    moveFolderNextTo,
    moveFoldersNextTo,
    moveDocToFolder,
    undoOrganization,
    redoOrganization,
  };
}
